import React from "react";
import { Link } from "react-router-dom";
import { useCompanyLogo } from "../../hooks/useCompanyLogo";

/**
 * CompanyLogo
 *
 * Generic component to display the company logo.
 * Internally uses `useCompanyLogo` to dynamically fetch the
 * logo from the backend, falling back to static constants
 * if unauthenticated or not found.
 *
 * @param {Object} props
 * @param {"full" | "icon"} [props.type="full"] Type of logo to render (full rectangular vs square icon)
 * @param {string} [props.className] Optional CSS class name for the wrapper element
 * @param {string} [props.imgClassName] Optional CSS class name for the image element specifically
 * @param {string} [props.href] Optional URL for link wrapper (renders an `a` or `Link` tag)
 * @param {React.CSSProperties} [props.style] Optional inline styles
 * @param {string} [props.alt] Alt text for the image
 * @param {string} [props.name] Optional name to render next to the logo (triggering group style)
 * @param {boolean} [props.plain=false] If true, renders just the img tag without any wrapper divs/links
 */
export const CompanyLogo = ({ type = "full", className = "", imgClassName = "", href, style, alt, name, plain = false }) => {
  const { fullLogo, iconLogo } = useCompanyLogo();
  const src = type === "icon" ? iconLogo : fullLogo;
  const computedAlt = alt || (type === "icon" ? "Company Icon" : "Company Logo");

  const imageElement = (
    <img src={src} alt={computedAlt} style={style} className={plain ? imgClassName || className : imgClassName} />
  );

  if (plain) {
    return imageElement;
  }

  const baseClass = name ? "mc-logo-group" : "mc-logo";
  const finalClassName = className ? `${baseClass} ${className}`.trim() : baseClass;

  const groupedImageElement = (
    <>
      {imageElement}
      {name && <span>{name}</span>}
    </>
  );

  if (href) {
    if (href.startsWith("http")) {
      return (
        <a href={href} className={finalClassName} style={{ display: "inline-block", ...style }}>
          {groupedImageElement}
        </a>
      );
    }
    return (
      <Link to={href} className={finalClassName} style={{ display: "inline-block", ...style }}>
        {groupedImageElement}
      </Link>
    );
  }

  return (
    <div className={finalClassName} style={{ display: "inline-block", ...style }}>
      {groupedImageElement}
    </div>
  );
};
